import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog";
import { trpc } from "~/lib/trpc";
import { PricingTable } from "~/routes/(app)/settings/-components/pricing-table";
import { PricingTableSkeleton } from "~/routes/(app)/settings/-components/pricing-table-skeleton";
import { SettingsTitle } from "~/routes/(app)/settings/-components/settings-title";
import { SubscriptionCard } from "~/routes/(app)/settings/-components/subscription-card";
import { UsageCard } from "~/routes/(app)/settings/-components/usage-card";
import { daysUntil } from "~/utils/date-utils";

export const Route = createFileRoute("/(app)/settings/billing")({
  component: RouteComponent,
  loader: async ({ context }) => {
    context.queryClient.ensureQueryData(trpc.stripe.getAllPlans.queryOptions());

    const [currentSubscription, usage] = await Promise.all([
      context.queryClient.ensureQueryData(
        trpc.stripe.getSubscription.queryOptions(),
      ),
      context.queryClient.ensureQueryData(
        trpc.usage.getCurrentUsage.queryOptions(),
      ),
    ]);

    return {
      currentSubscription,
      usage,
      crumb: { label: "Plans & Billing", options: [], id: "billing" },
    };
  },
});

function RouteComponent() {
  const { currentSubscription, usage } = Route.useLoaderData();
  const [isPricingOpen, setIsPricingOpen] = useState(false);

  const resetDays = daysUntil(
    currentSubscription.type === "paid"
      ? currentSubscription.subscription.currentPeriodEnd
      : currentSubscription.currentPeriodEnd,
  );

  return (
    <div className="space-y-6 w-full max-w-3xl">
      <SettingsTitle>Plans & Billing</SettingsTitle>

      <SubscriptionCard
        plan={currentSubscription}
        openPricingPlans={() => setIsPricingOpen(true)}
      />

      <div className="mt-5">
        <div className="mb-2 flex items-center gap-3 w-full">
          <h3 className="font-medium">Usage details</h3>
        </div>

        <div className="space-y-3">
          {/* All-time metrics */}
          <div className="bg-gray-100 rounded-xl p-1.5">
            <div className="mt-2 mb-2.5 mx-3">
              <h4 className="text-sm font-medium">All-time usage</h4>
              <p className="text-xs text-muted-foreground">
                These metrics represent your total usage and don't reset
              </p>
            </div>
            <div className="grid gap-1.5">
              <UsageCard
                title="Domains"
                limit={usage.limits.domains}
                current={usage.usage.domains}
              />
              <UsageCard
                title="Pages"
                limit={usage.limits.pages}
                current={usage.usage.pages}
              />
            </div>
          </div>

          {/* Periodic metrics */}
          <div className="bg-gray-100/75 rounded-xl p-1.5">
            <div className="mt-2 mb-2.5 mx-3 flex items-end justify-between">
              <div>
                <h4 className="text-sm font-medium">Monthly usage</h4>
                <p className="text-xs text-muted-foreground">
                  These metrics reset at the beginning of each billing cycle
                </p>
              </div>

              <p className="text-xs text-muted-foreground">
                Resets in {resetDays} day{resetDays === 1 ? "" : "s"}
              </p>
            </div>
            <div className="grid gap-2">
              <UsageCard
                title="Impressions"
                limit={usage.limits.impressions}
                current={usage.usage.impressions}
              />
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isPricingOpen} onOpenChange={setIsPricingOpen}>
        <VisuallyHidden>
          <DialogTitle>Pricing Table</DialogTitle>
        </VisuallyHidden>
        <DialogContent className="w-full !max-w-[1100px] !p-0" noCloseButton>
          <Suspense fallback={<PricingTableSkeleton />}>
            <PricingTable currentSubscription={currentSubscription} />
          </Suspense>
        </DialogContent>
      </Dialog>
    </div>
  );
}
